import { Button, Checkbox, Empty, Flex, Typography } from 'antd'
import type { FC } from 'react'
import styled from 'styled-components'

const { Text } = Typography

const Section = styled(Flex)`
  padding-bottom: 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const ColumnList = styled.div`
  max-height: 14rem;
  overflow-y: auto;
  padding-left: 1.5rem;
  font-size: 0.75rem;
`

export interface NiiSheetColumnPickerProps {
  label: string
  description: string
  /** Every column this sheet can produce for the current scope. */
  columns: ReadonlyArray<string>
  /** Columns that cannot be deselected while the sheet is included. */
  requiredColumns: ReadonlyArray<string>
  included: boolean
  selectedColumns: ReadonlyArray<string>
  onIncludedChange: (included: boolean) => void
  onSelectedColumnsChange: (columns: ReadonlyArray<string>) => void
}

/**
 * One sheet's include toggle and column checkboxes.
 *
 * Prop-based leaf: it knows nothing about how the selection is stored, only
 * which columns exist, which are checked, and which can't be unchecked.
 */
export const NiiSheetColumnPicker: FC<NiiSheetColumnPickerProps> = ({
  label,
  description,
  columns,
  requiredColumns,
  included,
  selectedColumns,
  onIncludedChange,
  onSelectedColumnsChange,
}) => {
  const selectableCount = columns.filter(
    (column) => !requiredColumns.includes(column)
  ).length
  const selectedSelectableCount = selectedColumns.filter(
    (column) => !requiredColumns.includes(column)
  ).length

  return (
    <Section vertical gap='small'>
      <Flex justify='space-between' align='center' gap='small'>
        <Checkbox
          checked={included}
          onChange={({ target }) => onIncludedChange(target.checked)}>
          <Text strong>{label}</Text>{' '}
          <Text type='secondary'>— {description}</Text>
        </Checkbox>
        <Flex gap='small'>
          <Button
            type='link'
            size='small'
            disabled={!included || selectedSelectableCount === selectableCount}
            onClick={() => onSelectedColumnsChange(columns)}>
            All
          </Button>
          <Button
            type='link'
            size='small'
            disabled={!included || selectedSelectableCount === 0}
            onClick={() =>
              onSelectedColumnsChange(
                columns.filter((column) => requiredColumns.includes(column))
              )
            }>
            None
          </Button>
        </Flex>
      </Flex>

      {included && (
        <ColumnList>
          {columns.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description='No columns in the current scope'
            />
          ) : (
            <Checkbox.Group
              value={[...selectedColumns]}
              onChange={(values) =>
                onSelectedColumnsChange(values as ReadonlyArray<string>)
              }>
              <Flex vertical gap='0.25rem'>
                {columns.map((column) => {
                  const isRequired = requiredColumns.includes(column)
                  return (
                    <Checkbox key={column} value={column} disabled={isRequired}>
                      {column}
                      {isRequired && <Text type='secondary'> (key)</Text>}
                    </Checkbox>
                  )
                })}
              </Flex>
            </Checkbox.Group>
          )}
        </ColumnList>
      )}
    </Section>
  )
}
